import { useLocation } from "react-router-dom";
import {
  BreadCrumb,
  Banner,
  HeadingTitle,
  Button,
  SubHeadingTitle,
} from "../../../components/shared/index";
import styles from "./contact.module.css";
import {
  FaEnvelope,
  FaPhoneAlt,
  FaAddressBook,
} from "react-icons/fa";
import toast from "react-hot-toast";
import { useContactUsMutation } from "../../../queries/useContactUs";
import { useForm } from "react-hook-form";
import BackToTop from "../../../components/BackToTop/backtotop";

const Contact = () => {
  const { pathname } = useLocation();

  const parts = pathname.split("/");
  const newPath = parts[parts.length - 2];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      subject: "",
      message: "",
    },
  });

  const { mutateAsync: contactSend, isPending } =
    useContactUsMutation();

  const handleMessageSend = async (data) => {
    try {
      const response = await contactSend(data);

      toast.success(
        response?.data?.message || "Message sent successfully"
      );

      reset();
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Oops! Message send failed"
      );
    }
  };

  return (
    <section className={styles.contact__wrapper}>
      {/* Breadcrumb + Banner */}
      <BreadcrumbBanner
        newPath={newPath}
        heading="Have questions about your medicines, orders? Our support team is here to help"
        imageUrl="https://t3.ftcdn.net/jpg/05/99/10/78/240_F_599107807_kaOhz5mux2kdVfhKZzdnFeDPgZUSlL32.jpg"
      />

      {/* Map */}
      <MapLocation />

      {/* Contact Section */}
      <div className={styles.contactDetails__wrapper}>
        {/* Contact Form */}
        <div className={styles.contact__form}>
          <HeadingTitle
            text="Tell Us Your Message"
            style={styles.contact__title}
          />

          <form
            onSubmit={handleSubmit(handleMessageSend)}
            className={styles.contactForm}
          >
            {/* Name */}
            <div className={styles.form__group}>
              <label>
                Your Name <span>*</span>
              </label>

              <input
                type="text"
                placeholder="Enter your name"
                {...register("name", {
                  required: "Name is required",
                  pattern: {
                    value: /^[A-Za-z\s]+$/,
                    message: "Only alphabets are allowed",
                  },
                  minLength: {
                    value: 3,
                    message:
                      "Name must be at least 3 characters",
                  },
                  maxLength: {
                    value: 30,
                    message:
                      "Name cannot exceed 30 characters",
                  },
                })}
              />

              {errors?.name && (
                <p className={styles.error}>
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Email */}
            <div className={styles.form__group}>
              <label>
                Your Email <span>*</span>
              </label>

              <input
                type="email"
                placeholder="Enter your email"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message:
                      "Enter a valid email address",
                  },
                })}
              />

              {errors?.email && (
                <p className={styles.error}>
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Subject */}
            <div className={styles.form__group}>
              <label>Subject</label>

              <input
                type="text"
                placeholder="Enter subject"
                {...register("subject", {
                  required: "Subject is required",
                  pattern: {
                    value:
                      /^[A-Za-z0-9\s.,!?'"()\-@#&]+$/,
                    message: "Invalid subject format",
                  },
                  minLength: {
                    value: 3,
                    message:
                      "Subject must be at least 3 characters",
                  },
                  maxLength: {
                    value: 100,
                    message:
                      "Subject cannot exceed 100 characters",
                  },
                })}
              />

              {errors?.subject && (
                <p className={styles.error}>
                  {errors.subject.message}
                </p>
              )}
            </div>

            {/* Message */}
            <div className={styles.form__group}>
              <label>
                Your Message <span>*</span>
              </label>

              <textarea
                rows="5"
                placeholder="Type your message"
                {...register("message", {
                  required: "Message is required",
                  minLength: {
                    value: 10,
                    message:
                      "Message must be at least 10 characters",
                  },
                  maxLength: {
                    value: 500,
                    message:
                      "Message cannot exceed 500 characters",
                  },
                  pattern: {
                    value:
                      /^[A-Za-z0-9\s.,!?'"()\-@#&]+$/,
                    message:
                      "Invalid characters in message",
                  },
                })}
              />

              {errors?.message && (
                <p className={styles.error}>
                  {errors.message.message}
                </p>
              )}
            </div>

            {/* Submit */}
            <Button
              type="submit"
              title={isPending ? "Sending..." : "Send Message"}
              style={styles.submit__btn}
              disabled={isPending}
            />
          </form>
        </div>

        {/* Contact Information */}
        <div className={styles.contact__info}>
          <HeadingTitle
            text="Contact Us"
            style={styles.info__title}
          />

          <SubHeadingTitle
            subTitle="Thank you for shopping with us! For any concerns, suggestions, or support requests, please don't hesitate to contact us."
            style={styles.info__desc}
          />

          {/* Address */}
          <div className={styles.info__block}>
            <div className={styles.icon__wrapper}>
              <FaAddressBook className={styles.info__icon} />
            </div>

            <div className={styles.info__content}>
              <h4>Address</h4>
              <p>Kompally, Hyderabad - 500100.</p>
            </div>
          </div>

          {/* Phone */}
          <div className={styles.info__block}>
            <div className={styles.icon__wrapper}>
              <FaPhoneAlt className={styles.info__icon} />
            </div>

            <div className={styles.info__content}>
              <h4>Contact</h4>

              <p>
                <span>Phone:</span>{" "}
                <a
                  href="tel:+918019070584"
                  className={styles.phoneLink}
                >
                  +91 8019070584
                </a>
              </p>

              <p>
                <span>WhatsApp:</span>{" "}
                <a
                  href="https://wa.me/918019070584"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.whatsappLink}
                >
                  Chat on WhatsApp
                </a>
              </p>
            </div>
          </div>

          {/* Email */}
          <div className={styles.info__block}>
            <div className={styles.icon__wrapper}>
              <FaEnvelope className={styles.info__icon} />
            </div>

            <div className={styles.info__content}>
              <h4>Email</h4>

              <p>
                <a
                  href="mailto:info@vitaura.co.in"
                  className={styles.emailLink}
                >
                  info@vitaura.co.in
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* =========================
   BREADCRUMB + BANNER
========================= */

const BreadcrumbBanner = ({
  newPath,
  heading,
  imageUrl,
}) => (
  <>
    <BreadCrumb
      customPaths={[
        {
          label: "Contact",
          path: newPath,
        },
      ]}
    />

    <Banner
      heading={heading}
      imageUrl={imageUrl}
    />
  </>
);

/* =========================
   MAP
========================= */

const MapLocation = () => {
  return (
    <div className={styles.map__wrapper}>
      <iframe
        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d30434.261874534986!2d78.48394789999999!3d17.541720549999997!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb855e9adf4dcf%3A0x6cd4e902af254f87!2sKompally%2C%20Hyderabad%2C%20Telangana!5e0!3m2!1sen!2sin!4v1783505877034!5m2!1sen!2sin"
        width="800"
        height="600"
        style={{ border: 0 }}
        allowFullScreen
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        title="Vitaura location"
      />

      <BackToTop />
    </div>
  );
};

export default Contact;